import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { SPANS, spanMs, type SpanKey } from './channels.ts';

/* ==========================================================================
   One cursor, one window, every panel.

   THE BAR §5 is unambiguous: every live panel is bound to one scrubber. This
   is that binding — a context, so a card three levels down can report where
   the pointer is without the workspace threading a callback through every
   grid cell, and so a card lifted onto robot detail or teleop still finds
   the cursor it belongs to.

   Two instants, deliberately, because they answer different questions:

     HOVER      where the pointer is right now. Transient, and what the
                readouts quote while the operator sweeps a trace.
     PLAYHEAD   where the operator LEFT the cursor — pinned by a click, moved
                by ←/→, advanced by play. It survives the pointer leaving.

   `at` is hover ?? playhead, so sweeping reads out live and taking the
   pointer away keeps answering for the instant that was chosen instead of
   silently reverting to the newest sample.

   And LIVE is a mode, not a position. Live means the window's right edge is
   wall clock and moves; replay means the window is an absolute range the
   operator brushed and nothing about it moves until they say so. That is
   `palantir-gotham-facet-rail-timeline.png`'s `Go Live` and the greying
   `LIVE` pill in `formant-timeline-data-presence-scrubber.png`.
   ========================================================================== */

export interface CursorApi {
  /** The window every bound panel draws. Epoch ms. */
  from: number;
  to: number;
  /** True when the window's right edge tracks wall clock. */
  live: boolean;
  hover: number | null;
  playhead: number | null;
  /** The instant every readout quotes. */
  at: number | null;
  setHover: (t: number | null) => void;
  setPlayhead: (t: number | null) => void;
  /** Zoom to an absolute range — a brush release. Leaves live mode. */
  zoomTo: (from: number, to: number) => void;
  /** Back to the span's own window, tracking wall clock. */
  goLive: () => void;
}

const noop = () => {};

export const CursorCtx = createContext<CursorApi>({
  from: 0, to: 1, live: true, hover: null, playhead: null, at: null,
  setHover: noop, setPlayhead: noop, zoomTo: noop, goLive: noop,
});

export function useCursor(): CursorApi {
  return useContext(CursorCtx);
}

/* --------------------------------------------------------------------------
   The transport itself.
   -------------------------------------------------------------------------- */

export interface Transport extends CursorApi {
  span: SpanKey;
  setSpan: (s: SpanKey) => void;
  playing: boolean;
  rate: number;
  setPlaying: (p: boolean) => void;
  setRate: (r: number) => void;
  /** Move the playhead by an interval of window time. */
  step: (deltaMs: number) => void;
  /** True while an absolute brushed range is in force. */
  zoomed: boolean;
  /**
   * The span the window's DURATION currently is, or null for a brushed range
   * that is not one of them.
   *
   * Not the same question as `span`, and not the same question as `live`.
   * Dragging the scrub bar back six hours leaves a window that is still
   * exactly 6h — it just no longer ends at now — and a span strip that goes
   * blank there is the control disowning a window it perfectly describes.
   * What genuinely matches nothing is a brush: 1h 20m is not a span, and the
   * strip should say so.
   */
  windowSpan: SpanKey | null;
}

/** Replay rates that mean something over a day of 2-minute telemetry. */
export const RATES: readonly number[] = [1, 60, 600];

/**
 * `now` is the wall clock the live window's right edge follows — pass the
 * console's one-second ticker so every surface agrees on when now is.
 *
 * The playhead stops dead at the window's right edge rather than wrapping: a
 * replay that loops silently is a replay you cannot trust to have ended.
 */
export function useTransport(now: number, initialSpan: SpanKey = '6h'): Transport {
  const [span, setSpan] = useState<SpanKey>(initialSpan);
  const [zoom, setZoom] = useState<{ from: number; to: number } | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [playhead, setPlayhead] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(60);

  const live = zoom === null;
  const width = spanMs(span);
  const from = zoom ? zoom.from : now - width;
  const to = zoom ? zoom.to : now;

  const raf = useRef(0);
  const last = useRef(0);
  const head = useRef<number | null>(null);
  head.current = playhead;
  const edges = useRef({ from, to });
  edges.current = { from, to };

  useEffect(() => {
    if (!playing) return;
    last.current = performance.now();
    const tick = (ts: number) => {
      const dt = ts - last.current;
      last.current = ts;
      const { from: a, to: b } = edges.current;
      const next = (head.current ?? a) + dt * rate;
      if (next >= b) {
        setPlayhead(b);
        setPlaying(false);
        return;
      }
      setPlayhead(next);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [playing, rate]);

  const step = useCallback((deltaMs: number) => {
    setPlaying(false);
    const { from: a, to: b } = edges.current;
    setPlayhead((p) => Math.max(a, Math.min(b, (p ?? b) + deltaMs)));
  }, []);

  const zoomTo = useCallback((a: number, b: number) => {
    if (b - a < 1000) return;
    setPlaying(false);
    setZoom({ from: a, to: b });
  }, []);

  const goLive = useCallback(() => {
    setPlaying(false);
    setZoom(null);
    setPlayhead(null);
  }, []);

  /* WINDOW SIZE AND LIVE-VS-REPLAY ARE TWO CONTROLS, NOT ONE.

     This used to drop the absolute range and jump to now, so an operator
     reading an incident at 02:00 who wanted to see more of it around it was
     thrown back to the present by the button that says `24h`. The span sets
     the window's DURATION; the pill sets whether its right edge is wall
     clock. Pressing a span in replay keeps the edge the operator is looking
     at and re-cuts the window back from it — which is also what
     `palantir-gotham-facet-rail-timeline.png` means by pairing a rate
     control with an explicit `Go Live`. */
  const changeSpan = useCallback((s: SpanKey) => {
    setSpan(s);
    setPlaying(false);
    setZoom((z) => (z === null ? null : { from: z.to - spanMs(s), to: z.to }));
  }, []);

  return useMemo<Transport>(
    () => ({
      from, to, live, hover, playhead,
      at: hover ?? playhead,
      setHover, setPlayhead, zoomTo, goLive,
      span, setSpan: changeSpan,
      playing, rate, setPlaying, setRate, step,
      zoomed: zoom !== null,
      windowSpan: SPANS.find((s) => Math.abs(spanMs(s.key) - (to - from)) < 1000)?.key ?? null,
    }),
    [
      from, to, live, hover, playhead, zoomTo, goLive, span, changeSpan,
      playing, rate, step, zoom,
    ],
  );
}
